/**
 * AuthHandler - Main authentication orchestrator for GCP IAP integration.
 *
 * This component implements the gcip-iap AuthenticationHandler interface
 * and manages the complete login flow including:
 * - Email/password authentication
 * - Google OAuth
 * - SAML/OIDC federation
 * - Multi-factor authentication (verification and enrollment)
 * - Government usage consent
 * - Error handling and recovery
 *
 * @see https://www.npmjs.com/package/gcip-iap
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as ciap from 'gcip-iap';
import * as Sentry from '@sentry/react';
import { captureAuthError, logStepTransition } from 'utils/sentryUtils';
import { useSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';
import { useTitle } from 'react-use';

import {
    LoginCard,
    EmailStep,
    PasswordStep,
    ProviderButtons,
    MFAVerification,
    MFAEnrollment,
    MFABlocked,
    ConsentDialog,
    LoadingOverlay,
    ErrorDisplay,
    SignedOutMessage,
} from 'components/Login';

import {
    getFirebaseAuth,
    getSignInMethodsForEmail,
    signInWithEmail,
    signInWithGoogle,
    signInWithSAML,
    signInWithOIDC,
    getMFAResolver,
    sendMFAVerificationCode,
    completeMFAVerification,
    hasUserEnrolledMFA,
    getUserPhoneForMFA,
    startMFAEnrollment,
    completeMFAEnrollment,
    createRecaptchaVerifier,
    maskPhoneNumber,
} from 'services/authService';

import { fetchMaintenanceStatus } from 'services/maintenanceService';
import { signOut } from 'firebase/auth';
import { MockAuthentication } from 'mocks/MockIap';

import {
    AUTH_STEPS,
    CONFIG,
    LOADING_MESSAGES,
    getErrorMessage,
    isEmailExemptFromMFA,
} from 'constants/constants';

/**
 * Main authentication handler component.
 * Implements gcip-iap AuthenticationHandler interface and manages login flow.
 *
 * @returns {React.ReactElement}
 */
const AuthHandler = () => {
    useTitle('Sign In');
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();

    // ============================================================
    // State Management
    // ============================================================

    /** Current step in the authentication flow */
    const [currentStep, setCurrentStep] = useState(AUTH_STEPS.LOADING);

    /** User's email address */
    const [email, setEmail] = useState('');

    /** Available sign-in methods for the current email */
    const [signInMethods, setSignInMethods] = useState({
        hasPassword: false,
        hasGoogle: false,
        samlProviders: [],
        oidcProviders: [],
    });

    /** Loading states */
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES.CHECKING_EMAIL);
    const [loadingProvider, setLoadingProvider] = useState(null);

    /** Error state */
    const [error, setError] = useState(null);
    const [fieldError, setFieldError] = useState('');

    /** MFA state */
    const [mfaResolver, setMfaResolver] = useState(null);
    const [mfaPhoneHint, setMfaPhoneHint] = useState(null);
    const [mfaVerificationId, setMfaVerificationId] = useState(null);
    const [mfaIsResending, setMfaIsResending] = useState(false);

    /** MFA enrollment state */
    const [enrollmentUser, setEnrollmentUser] = useState(null);
    const [enrollmentVerificationId, setEnrollmentVerificationId] = useState(null);
    const [enrollmentComplete, setEnrollmentComplete] = useState(false);

    /** Consent dialog state */
    const [showConsent, setShowConsent] = useState(false);
    const [pendingCredential, setPendingCredential] = useState(null);
    
    /** Pending password for consent flow (password submitted, awaiting consent) */
    const [pendingPassword, setPendingPassword] = useState(null);

    /** Maintenance status */
    const [maintenanceStatus, setMaintenanceStatus] = useState({ state: 'NONE', message: '', startTime: '', endTime: '' });

    /** gcip-iap instance and promise resolvers */
    const authenticationRef = useRef(null);
    const authInstanceRef = useRef(null);
    const signInResolverRef = useRef(null);
    const signInRejecterRef = useRef(null);

    /** Tracks if user authenticated with email/password (for MFA enrollment) */
    const usedEmailPasswordRef = useRef(false);

    /** Stores reject function for MFA blocked promise */
    const mfaBlockedRejecterRef = useRef(null);

    /** reCAPTCHA verifier */
    const recaptchaVerifierRef = useRef(null);

    // ============================================================
    // Traceability & Logging
    // ============================================================

    /**
     * Log transitions between authentication steps for traceability.
     * Minimally invasive: Does not affect the stability of gcip-iap callbacks.
     */
    useEffect(() => {
        logStepTransition(currentStep, email);
    }, [currentStep, email]);

    // ============================================================
    // gcip-iap AuthenticationHandler Implementation
    // ============================================================

    /**
     * Gets Firebase Auth instance for the given API key and tenant.
     * @param {string} apiKey - Firebase API key
     * @param {string|null} tenantId - Tenant ID
     * @returns {import('firebase/auth').Auth}
     */
    const getAuth = useCallback((apiKey, tenantId) => {
        const auth = getFirebaseAuth(apiKey, tenantId);
        authInstanceRef.current = auth;
        return auth;
    }, []);

    /**
     * Starts the sign-in process.
     * Returns a Promise that resolves when user completes authentication.
     * @param {import('firebase/auth').Auth} auth - Auth instance
     * @param {import('gcip-iap').SelectedTenantInfo} [match] - Tenant info
     * @returns {Promise<import('firebase/auth').UserCredential>}
     */
    const startSignIn = useCallback((auth, match) => {
        authInstanceRef.current = auth;

        // Pre-fill email if provided
        if (match?.email) {
            setEmail(match.email);
        }

        setIsLoading(false);
        setCurrentStep(AUTH_STEPS.EMAIL_STEP);

        // Return a promise that will be resolved when login completes
        return new Promise((resolve, reject) => {
            signInResolverRef.current = resolve;
            signInRejecterRef.current = reject;
        });
    }, []);

    /**
     * Handles sign-out completion.
     * @returns {Promise<void>}
     */
    const completeSignOut = useCallback(async () => {
        setIsLoading(false);
        setCurrentStep(AUTH_STEPS.SIGNED_OUT);
    }, []);

    /**
     * Processes user after authentication (MFA checks, etc.).
     * @param {import('firebase/auth').User} user - Authenticated user
     * @returns {Promise<import('firebase/auth').User>}
     */
    const processUser = useCallback(async (user) => {
        // Identify user in Sentry with full context
        Sentry.setUser({
            id: user.uid,
            email: user.email,
        });
        
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User successfully authenticated',
            level: 'info',
            data: { 
                uid: user.uid, 
                email: user.email,
                isAnonymous: user.isAnonymous,
                providerId: user.providerId
            }
        });

        // MFA enrollment check only applies to email/password authentication
        // Third-party providers (Google, SAML, OIDC) handle their own MFA
        if (!usedEmailPasswordRef.current) {
            return user;
        }

        // Check if user needs MFA enrollment
        if (!hasUserEnrolledMFA(user) && !isEmailExemptFromMFA(user.email)) {
            const phoneNumber = getUserPhoneForMFA(user);

            if (phoneNumber) {
                // User has phone, initiate enrollment
                setEnrollmentUser(user);
                setMfaPhoneHint({ phoneNumber });
                setCurrentStep(AUTH_STEPS.MFA_ENROLLMENT);
                setIsLoading(false); // Reset loading state for enrollment UI

                // Wait for enrollment to complete
                return new Promise((resolve) => {
                    const checkEnrollment = setInterval(() => {
                        if (enrollmentComplete) {
                            clearInterval(checkEnrollment);
                            resolve(user);
                        }
                    }, 500);
                });
            } else {
                // No phone number configured - BLOCK the user (security requirement)
                Sentry.captureMessage('User blocked: MFA required but no phone number configured', {
                    level: 'warning',
                    user: { email: user.email },
                    tags: { auth_step: 'MFA_ENROLLMENT_CHECK' }
                });
                setCurrentStep(AUTH_STEPS.MFA_BLOCKED);
                setIsLoading(false);

                // Return a promise that can be rejected when user signs out
                return new Promise((resolve, reject) => {
                    // Store reject function so we can call it when user signs out
                    mfaBlockedRejecterRef.current = reject;
                });
            }
        }

        return user;
    }, [enrollmentComplete]);

    /**
     * Handles authentication errors.
     * @param {Error} error - Error object
     */
    const handleError = useCallback((error) => {
        // captureAuthError uses global Sentry scope, set early in handleEmailSubmit
        captureAuthError(error);

        const message = getErrorMessage(error.code);
        setError({
            message,
            canRetry: !!error.retry,
            retryFn: error.retry,
        });
        setCurrentStep(AUTH_STEPS.ERROR);
        setIsLoading(false);
    }, []);

    /**
     * Shows the progress/loading overlay.
     */
    const showProgressBar = useCallback(() => {
        setIsLoading(true);
    }, []);

    /**
     * Hides the progress/loading overlay.
     */
    const hideProgressBar = useCallback(() => {
        setIsLoading(false);
    }, []);

    // ============================================================
    // Initialize gcip-iap and Maintenance Check
    // ============================================================

    useEffect(() => {
        // Fetch maintenance status
        const checkMaintenance = async () => {
            const status = await fetchMaintenanceStatus();
            setMaintenanceStatus(status);
        };
        checkMaintenance();
    }, []);

    useEffect(() => {
        const authHandler = {
            getAuth,
            startSignIn,
            completeSignOut,
            processUser,
            handleError,
            showProgressBar,
            hideProgressBar,
        };

        const AuthenticationClass = (import.meta.env.VITE_USE_MOCK_IAP === 'true' && import.meta.env.DEV)
            ? MockAuthentication
            : ciap.Authentication;

        const authentication = new AuthenticationClass(authHandler);
        authenticationRef.current = authentication;

        // Start the authentication flow
        authentication.start();

        // Cleanup
        return () => {
            if (recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current.clear();
            }
        };
    }, [getAuth, startSignIn, completeSignOut, processUser, handleError, showProgressBar, hideProgressBar]);

    // ============================================================
    // reCAPTCHA Setup
    // ============================================================

    /**
     * Initializes reCAPTCHA verifier for phone auth.
     * Must be called with await as it needs to render the verifier.
     * @param {import('firebase/auth').Auth} [auth] - Optional auth instance to use (e.g., resolver.auth for MFA)
     * @returns {Promise<RecaptchaVerifier|null>}
     */
    const initRecaptcha = useCallback(async (auth = null) => {
        const authToUse = auth || authInstanceRef.current;

        // If we have a cached verifier but it's for a different auth instance,
        // clear it and create a new one (required for MFA with resolver.auth)
        if (recaptchaVerifierRef.current && auth) {
            try {
                recaptchaVerifierRef.current.clear();
            } catch {
                // Ignore clear errors
            }
            recaptchaVerifierRef.current = null;
        }

        if (recaptchaVerifierRef.current) {
            return recaptchaVerifierRef.current;
        }

        if (!authToUse) {
            console.warn('Auth instance not ready for reCAPTCHA');
            return null;
        }

        try {
            const verifier = createRecaptchaVerifier(
                authToUse,
                'recaptcha-container',
                () => {
                    // reCAPTCHA expired, reset it
                    recaptchaVerifierRef.current = null;
                }
            );

            // IMPORTANT: Must render the verifier before using it
            await verifier.render();

            recaptchaVerifierRef.current = verifier;
            return verifier;
        } catch (err) {
            captureAuthError(err, { action: 'init_recaptcha' });
            return null;
        }
    }, []);

    // ============================================================
    // Email Step Handlers
    // ============================================================

    /**
     * Handles email submission - fetches available sign-in methods.
     * @param {string} submittedEmail - User's email
     */
    const handleEmailSubmit = async (submittedEmail) => {
        setEmail(submittedEmail);
        setFieldError('');
        setIsLoading(true);
        setLoadingMessage(LOADING_MESSAGES.CHECKING_EMAIL);

        // Identify email early for traceability in global Sentry scope
        Sentry.setUser({ email: submittedEmail });

        try {
            let methods = await getSignInMethodsForEmail(
                authInstanceRef.current,
                submittedEmail
            );

            // SPECIAL CASE: Trust @secunetics.com domain.
            // If the email is Secunetics, we ALWAYS allow Google sign-in even if Firebase
            // returns no methods (which happens due to Email Enumeration Protection).
            const isSecunetics = submittedEmail.toLowerCase().endsWith('@secunetics.com');
            if (isSecunetics) {
                methods = {
                    ...methods,
                    hasGoogle: true
                };
            }

            setSignInMethods(methods);

            // Determine next step based on available methods
            const hasThirdParty = methods.hasGoogle ||
                methods.samlProviders.length > 0 ||
                methods.oidcProviders.length > 0;

            if (methods.hasPassword) {
                // Password flow (optionally with third-party buttons below)
                setCurrentStep(AUTH_STEPS.PASSWORD_STEP);
            } else if (hasThirdParty) {
                // Only third-party providers
                setCurrentStep(AUTH_STEPS.PROVIDER_SELECTION);
            } else {
                // No methods found - show generic error (FR-3.1.4)
                setFieldError('Invalid email address');
            }
        } catch (err) {
            captureAuthError(err);
            setFieldError(getErrorMessage(err.code));
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Resets to email step (e.g., when user wants to use different email).
     * Clears ALL state to ensure a clean restart of the auth flow.
     */
    const handleBackToEmail = () => {
        // Clear Sentry user context
        Sentry.setUser(null);

        // Clear email and form errors
        setEmail('');
        setFieldError('');
        setError(null);

        // Reset sign-in methods
        setSignInMethods({
            hasPassword: false,
            hasGoogle: false,
            samlProviders: [],
            oidcProviders: [],
        });

        // Clear loading states
        setIsLoading(false);
        setLoadingProvider(null);

        // Clear MFA verification state
        setMfaResolver(null);
        setMfaPhoneHint(null);
        setMfaVerificationId(null);
        setMfaIsResending(false);

        // Clear MFA enrollment state
        setEnrollmentUser(null);
        setEnrollmentVerificationId(null);
        setEnrollmentComplete(false);

        // Clear consent/pending state
        setShowConsent(false);
        setPendingCredential(null);
        setPendingPassword(null);

        // Reset refs
        usedEmailPasswordRef.current = false;

        // Clear reCAPTCHA if it exists
        if (recaptchaVerifierRef.current) {
            try {
                recaptchaVerifierRef.current.clear();
            } catch {
                // Ignore clear errors
            }
            recaptchaVerifierRef.current = null;
        }

        // Finally, navigate back to email step
        setCurrentStep(AUTH_STEPS.EMAIL_STEP);
    };

    /**
     * Handles "start over" from mid-authentication flow (MFA steps).
     * Must sign out from Firebase first (since user is partially authenticated),
     * then do a full page reload because gcip-iap's pending promise
     * cannot be cleanly reset without restarting the entire flow.
     */
    const handleStartOver = async () => {
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User requested to start over',
            level: 'info'
        });
        Sentry.setUser(null);

        // Sign out from Firebase first to clear partial authentication
        if (authInstanceRef.current) {
            try {
                await signOut(authInstanceRef.current);
            } catch (err) {
                captureAuthError(err);
            }
        }
        window.location.reload();
    };

    // ============================================================
    // Password Authentication
    // ============================================================

    /**
     * Handles password submission for email/password auth.
     * Shows consent dialog BEFORE making the login request (FR-2.3.1).
     * @param {string} password - User's password
     */
    const handlePasswordSubmit = (password) => {
        setFieldError('');
        
        // Store password and show consent dialog BEFORE making login request
        // This ensures users see the government system notice before authentication
        setPendingPassword(password);
        setShowConsent(true);
    };
    
    /**
     * Performs the actual email/password authentication.
     * Called after user accepts consent dialog.
     * @param {string} password - User's password
     */
    const performEmailPasswordSignIn = async (password) => {
        setIsLoading(true);
        setLoadingMessage(LOADING_MESSAGES.SIGNING_IN);

        try {
            const userCredential = await signInWithEmail(
                authInstanceRef.current,
                email,
                password
            );

            // Mark that user used email/password (for MFA enrollment check)
            usedEmailPasswordRef.current = true;

            // Complete the sign-in flow
            completeSignInFlow(userCredential);
        } catch (err) {
            captureAuthError(err);

            // Check for MFA required
            if (err.code === 'auth/multi-factor-auth-required') {
                await handleMFARequired(err);
            } else {
                setFieldError(getErrorMessage(err.code));
                setIsLoading(false);
            }
        }
    };

    // ============================================================
    // Third-Party Authentication
    // ============================================================

    /**
     * Handles Google sign-in.
     */
    const handleGoogleSignIn = async () => {
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User clicked Sign in with Google',
            level: 'info'
        });
        setLoadingProvider('google.com');
        setLoadingMessage(LOADING_MESSAGES.SIGNING_IN_GOOGLE);

        try {
            const userCredential = await signInWithGoogle(
                authInstanceRef.current,
                email
            );

            // Resolve sign-in (no consent needed for third-party)
            completeSignInFlow(userCredential);
        } catch (err) {
            captureAuthError(err);

            if (err.code === 'auth/multi-factor-auth-required') {
                await handleMFARequired(err);
            } else {
                enqueueSnackbar(getErrorMessage(err.code), { variant: 'error' });
            }
        } finally {
            setLoadingProvider(null);
        }
    };

    /**
     * Handles SAML provider sign-in.
     * @param {string} providerId - SAML provider ID
     */
    const handleSAMLSignIn = async (providerId) => {
        Sentry.addBreadcrumb({
            category: 'auth',
            message: `User clicked SAML provider: ${providerId}`,
            level: 'info'
        });
        setLoadingProvider(providerId);
        setLoadingMessage(LOADING_MESSAGES.SIGNING_IN_SAML);

        try {
            const userCredential = await signInWithSAML(
                authInstanceRef.current,
                providerId
            );

            completeSignInFlow(userCredential);
        } catch (err) {
            captureAuthError(err, { providerId });

            if (err.code === 'auth/multi-factor-auth-required') {
                await handleMFARequired(err);
            } else {
                enqueueSnackbar(getErrorMessage(err.code), { variant: 'error' });
            }
        } finally {
            setLoadingProvider(null);
        }
    };

    /**
     * Handles OIDC provider sign-in.
     * @param {string} providerId - OIDC provider ID
     */
    const handleOIDCSignIn = async (providerId) => {
        Sentry.addBreadcrumb({
            category: 'auth',
            message: `User clicked OIDC provider: ${providerId}`,
            level: 'info'
        });
        setLoadingProvider(providerId);
        setLoadingMessage(LOADING_MESSAGES.SIGNING_IN_OIDC);

        try {
            const userCredential = await signInWithOIDC(
                authInstanceRef.current,
                providerId
            );

            completeSignInFlow(userCredential);
        } catch (err) {
            captureAuthError(err, { providerId });

            if (err.code === 'auth/multi-factor-auth-required') {
                await handleMFARequired(err);
            } else {
                enqueueSnackbar(getErrorMessage(err.code), { variant: 'error' });
            }
        } finally {
            setLoadingProvider(null);
        }
    };

    // ============================================================
    // MFA Verification
    // ============================================================

    /**
     * Handles MFA required error - initiates MFA verification flow.
     * @param {Error} error - Firebase auth error
     */
    const handleMFARequired = async (error) => {
        try {
            const { resolver, hints } = getMFAResolver(authInstanceRef.current, error);
            setMfaResolver(resolver);

            // Find phone factor hint
            const phoneHint = hints.find(
                h => h.factorId === 'phone'
            );

            if (phoneHint) {
                setMfaPhoneHint(phoneHint);

                // Initialize reCAPTCHA with the same auth instance used for sign-in
                // (RecaptchaVerifier and PhoneAuthProvider must use the same auth)
                const verifier = await initRecaptcha(authInstanceRef.current);
                if (verifier) {
                    const verificationId = await sendMFAVerificationCode(
                        authInstanceRef.current,
                        resolver,
                        phoneHint,
                        verifier
                    );
                    setMfaVerificationId(verificationId);
                    setCurrentStep(AUTH_STEPS.MFA_VERIFICATION);
                    setIsLoading(false);
                }
            } else {
                throw new Error('No phone factor enrolled');
            }
        } catch (err) {
            captureAuthError(err);
            setError({
                message: getErrorMessage(err.code) || 'Failed to initiate MFA verification.',
                canRetry: true,
                retryFn: handleBackToEmail,
            });
            setCurrentStep(AUTH_STEPS.ERROR);
            setIsLoading(false);
        }
    };

    /**
     * Handles MFA code verification.
     * @param {string} code - 6-digit verification code
     */
    const handleMFAVerify = async (code) => {
        setFieldError('');
        setIsLoading(true);
        setLoadingMessage(LOADING_MESSAGES.VERIFYING_CODE);

        try {
            const userCredential = await completeMFAVerification(
                mfaResolver,
                mfaVerificationId,
                code
            );

            completeSignInFlow(userCredential);
        } catch (err) {
            captureAuthError(err);
            setFieldError(getErrorMessage(err.code));
            setIsLoading(false);
        }
    };

    /**
     * Handles resending MFA verification code.
     */
    const handleMFAResend = async () => {
        setMfaIsResending(true);
        setFieldError('');

        try {
            // Reset reCAPTCHA
            if (recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current.clear();
                recaptchaVerifierRef.current = null;
            }

            const verifier = await initRecaptcha(authInstanceRef.current);
            if (verifier) {
                const verificationId = await sendMFAVerificationCode(
                    authInstanceRef.current,
                    mfaResolver,
                    mfaPhoneHint,
                    verifier
                );
                setMfaVerificationId(verificationId);
                enqueueSnackbar('Verification code sent!', { variant: 'success' });
            }
        } catch (err) {
            captureAuthError(err);
            enqueueSnackbar(getErrorMessage(err.code), { variant: 'error' });
        } finally {
            setMfaIsResending(false);
        }
    };

    // ============================================================
    // MFA Enrollment
    // ============================================================

    /**
     * Handles sending enrollment verification code.
     * @returns {Promise<boolean>} True if code was sent successfully
     */
    const handleEnrollmentSendCode = async () => {
        setFieldError('');
        setIsLoading(true);
        setLoadingMessage(LOADING_MESSAGES.ENROLLING_MFA);

        try {
            const verifier = await initRecaptcha();
            if (verifier && enrollmentUser && mfaPhoneHint?.phoneNumber) {
                const verificationId = await startMFAEnrollment(
                    enrollmentUser,
                    mfaPhoneHint.phoneNumber,
                    verifier
                );
                setEnrollmentVerificationId(verificationId);
                setIsLoading(false);
                return true;
            } else {
                setFieldError('Unable to initialize verification. Please try again.');
                setIsLoading(false);
                return false;
            }
        } catch (err) {
            captureAuthError(err);
            setFieldError(getErrorMessage(err.code));
            setIsLoading(false);
            return false;
        }
    };

    /**
     * Handles enrollment code verification.
     * @param {string} code - 6-digit verification code
     */
    const handleEnrollmentVerify = async (code) => {
        setFieldError('');
        setIsLoading(true);
        setLoadingMessage(LOADING_MESSAGES.ENROLLING_MFA);

        try {
            await completeMFAEnrollment(
                enrollmentUser,
                enrollmentVerificationId,
                code
            );

            setEnrollmentComplete(true);
            enqueueSnackbar('MFA enabled successfully!', { variant: 'success' });

            // Continue with original sign-in flow after delay
            setTimeout(() => {
                setIsLoading(true);
                setLoadingMessage(LOADING_MESSAGES.REDIRECTING);
            }, 2000);
        } catch (err) {
            captureAuthError(err);
            setFieldError(getErrorMessage(err.code));
            setIsLoading(false);
        }
    };

    // ============================================================
    // Consent Dialog
    // ============================================================

    /**
     * Handles consent acceptance.
     * For email/password flow, this triggers the actual authentication.
     */
    const handleConsentAccept = () => {
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User accepted government consent',
            level: 'info'
        });

        setShowConsent(false);
        
        // If we have a pending password, this is email/password flow
        // Perform the actual authentication now that consent is accepted
        if (pendingPassword) {
            const password = pendingPassword;
            setPendingPassword(null);
            performEmailPasswordSignIn(password);
        } else if (pendingCredential) {
            // Legacy path (should not be reached in normal flow)
            completeSignInFlow(pendingCredential);
        }
    };

    /**
     * Handles consent decline.
     * Since consent is shown BEFORE authentication, no sign-out is needed.
     */
    const handleConsentDecline = async () => {
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User declined government consent',
            level: 'warning'
        });

        setShowConsent(false);
        setPendingPassword(null);
        setPendingCredential(null);

        try {
            await confirm({
                title: 'Access Denied',
                description: 'You must accept the terms of use to access this system. You will be redirected to the application.',
                confirmationText: 'OK',
                hideCancelButton: true,
            });
        } catch {
            // Dialog dismissed
        }

        // Redirect to default app URL
        window.location.href = CONFIG.DEFAULT_APP_URL;
    };

    // ============================================================
    // Sign-In Flow Completion
    // ============================================================

    /**
     * Completes the sign-in flow by resolving the gcip-iap promise.
     * @param {import('firebase/auth').UserCredential} userCredential - Authenticated credential
     */
    const completeSignInFlow = (userCredential) => {
        setIsLoading(true);
        setLoadingMessage(LOADING_MESSAGES.REDIRECTING);

        // Resolve the promise returned by startSignIn
        if (signInResolverRef.current) {
            signInResolverRef.current(userCredential);
            signInResolverRef.current = null;
            signInRejecterRef.current = null;
        }
    };

    /**
     * Handles error retry action.
     */
    const handleErrorRetry = () => {
        if (error?.retryFn) {
            error.retryFn();
        } else {
            handleBackToEmail();
        }
    };

    /**
     * Handles sign out from MFA blocked state.
     * Must properly sign out from Firebase and reject the pending promise so gcip-iap can restart.
     */
    const handleMFABlockedSignOut = async () => {
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User signed out from MFA blocked state',
            level: 'warning'
        });
        Sentry.setUser(null);

        // Sign out from Firebase first to clear the session
        if (authInstanceRef.current) {
            try {
                await signOut(authInstanceRef.current);
            } catch (err) {
                captureAuthError(err);
            }
        }

        // Reject the pending processUser promise so gcip-iap can restart
        if (mfaBlockedRejecterRef.current) {
            mfaBlockedRejecterRef.current(new Error('User signed out from MFA blocked state'));
            mfaBlockedRejecterRef.current = null;
        }

        // Reset state and trigger a page reload to restart gcip-iap flow
        usedEmailPasswordRef.current = false;
        window.location.reload();
    };

    /**
     * Handles "Return" from error display.
     * Signs out from Firebase first to prevent auto-login on next visit.
     */
    const handleErrorGoHome = async () => {
        Sentry.setUser(null);

        // Sign out from Firebase first to prevent auto-login on next visit.
        if (authInstanceRef.current) {
            try {
                await signOut(authInstanceRef.current);
            } catch (err) {
                captureAuthError(err);
            }
        }

        // Redirect to default app URL
        window.location.href = CONFIG.DEFAULT_APP_URL;
    };

    // ============================================================
    // Render
    // ============================================================

    /**
     * Renders the appropriate step content.
     * @returns {React.ReactElement}
     */
    const renderStepContent = () => {
        // If system is down, don't render any login steps
        if (maintenanceStatus.state === 'DOWN') {
            return null;
        }

        const hasThirdParty = signInMethods.hasGoogle ||
            signInMethods.samlProviders.length > 0 ||
            signInMethods.oidcProviders.length > 0;

        switch (currentStep) {
            case AUTH_STEPS.EMAIL_STEP:
                return (
                    <EmailStep
                        onSubmit={handleEmailSubmit}
                        isLoading={isLoading}
                        defaultEmail={email}
                        errorMessage={fieldError}
                        onClearError={() => setFieldError('')}
                    />
                );

            case AUTH_STEPS.PASSWORD_STEP:
                return (
                    <>
                        <PasswordStep
                            email={email}
                            onSubmit={handlePasswordSubmit}
                            onBack={handleBackToEmail}
                            isLoading={isLoading}
                            errorMessage={fieldError}
                            onClearError={() => setFieldError('')}
                        />
                        {hasThirdParty && (
                            <ProviderButtons
                                hasGoogle={signInMethods.hasGoogle}
                                samlProviders={signInMethods.samlProviders}
                                oidcProviders={signInMethods.oidcProviders}
                                onGoogleClick={handleGoogleSignIn}
                                onSAMLClick={handleSAMLSignIn}
                                onOIDCClick={handleOIDCSignIn}
                                isLoading={!!loadingProvider}
                                loadingProvider={loadingProvider}
                                showDivider
                            />
                        )}
                    </>
                );

            case AUTH_STEPS.PROVIDER_SELECTION:
                return (
                    <ProviderButtons
                        hasGoogle={signInMethods.hasGoogle}
                        samlProviders={signInMethods.samlProviders}
                        oidcProviders={signInMethods.oidcProviders}
                        onGoogleClick={handleGoogleSignIn}
                        onSAMLClick={handleSAMLSignIn}
                        onOIDCClick={handleOIDCSignIn}
                        isLoading={!!loadingProvider}
                        loadingProvider={loadingProvider}
                        onBack={handleBackToEmail}
                    />
                );

            case AUTH_STEPS.MFA_VERIFICATION:
                return (
                    <MFAVerification
                        phoneNumber={maskPhoneNumber(mfaPhoneHint?.phoneNumber)}
                        onSubmit={handleMFAVerify}
                        onResend={handleMFAResend}
                        isLoading={isLoading}
                        isResending={mfaIsResending}
                        errorMessage={fieldError}
                        onBack={handleStartOver}
                        onClearError={() => setFieldError('')}
                    />
                );

            case AUTH_STEPS.MFA_ENROLLMENT:
                return (
                    <MFAEnrollment
                        phoneNumber={maskPhoneNumber(mfaPhoneHint?.phoneNumber)}
                        onSendCode={handleEnrollmentSendCode}
                        onVerify={handleEnrollmentVerify}
                        isLoading={isLoading}
                        codeSent={!!enrollmentVerificationId}
                        isComplete={enrollmentComplete}
                        errorMessage={fieldError}
                        onBack={handleStartOver}
                        onClearError={() => setFieldError('')}
                    />
                );

            case AUTH_STEPS.ERROR:
                return (
                    <ErrorDisplay
                        message={error?.message || 'An unexpected error occurred.'}
                        onRetry={handleErrorRetry}
                        canRetry={error?.canRetry}
                        onGoHome={handleErrorGoHome}
                    />
                );

            case AUTH_STEPS.MFA_BLOCKED:
                return (
                    <MFABlocked
                        onSignOut={handleMFABlockedSignOut}
                    />
                );

            case AUTH_STEPS.SIGNED_OUT:
                return <SignedOutMessage />;

            case AUTH_STEPS.LOADING:
            default:
                return null;
        }
    };

    /**
     * Gets the appropriate title for the current step.
     * @returns {string}
     */
    const getStepTitle = () => {
        switch (currentStep) {
            case AUTH_STEPS.MFA_VERIFICATION:
                return 'Verify Your Identity';
            case AUTH_STEPS.MFA_ENROLLMENT:
                return 'Enable MFA';
            case AUTH_STEPS.MFA_BLOCKED:
                return 'Access Blocked';
            case AUTH_STEPS.ERROR:
                return 'Oops!';
            case AUTH_STEPS.SIGNED_OUT:
                return 'Signed Out';
            default:
                return 'Sign In';
        }
    };

    /**
     * Gets the severity level for the LoginCard based on current step.
     * @returns {'error'|'warning'|'info'|'success'|undefined}
     */
    const getSeverity = () => {
        // Maintenance states take precedence
        if (maintenanceStatus.state === 'DOWN') {
            return 'error';
        }
        
        // Error states
        if (currentStep === AUTH_STEPS.ERROR || currentStep === AUTH_STEPS.MFA_BLOCKED) {
            return 'error';
        }
        // Field-level errors show error severity
        if (fieldError) {
            return 'error';
        }
        
        if (maintenanceStatus.state === 'WARNING') {
            return 'warning';
        }

        // MFA verification shows info severity
        if (currentStep === AUTH_STEPS.MFA_VERIFICATION) {
            return 'info';
        }
        // MFA enrollment shows warning (enrollment required), or success (enrollment complete)
        if (currentStep === AUTH_STEPS.MFA_ENROLLMENT) {
            return enrollmentComplete ? 'success' : 'warning';
        }
        // Default - no severity
        return undefined;
    };

    return (
        <>
            <LoginCard
                title={maintenanceStatus.state === 'DOWN' ? 'System Maintenance' : getStepTitle()}
                severity={getSeverity()}
                maintenanceMessage={maintenanceStatus.state !== 'NONE' ? maintenanceStatus : undefined}
            >
                {renderStepContent()}
            </LoginCard>

            {/* Consent Dialog */}
            <ConsentDialog
                open={showConsent}
                onAccept={handleConsentAccept}
                onDecline={handleConsentDecline}
                isLoading={isLoading}
            />

            {/* Loading Overlay */}
            <LoadingOverlay
                open={isLoading && currentStep === AUTH_STEPS.LOADING}
                message={loadingMessage}
            />

            {/* Hidden reCAPTCHA container */}
            <div id="recaptcha-container" style={{ display: 'none' }} />
        </>
    );
};

export default AuthHandler;
